import { useState, useEffect } from "react";
import {
    Modal,
    ModalDialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Textarea,
    Box,
    Typography,
    Divider,
    Skeleton,
} from "@mui/joy";
import { Button } from "../../shared";
import { MessageSquareText, X } from "lucide-react";

interface ImageUploadModalProps {
    open: boolean;
    files: File[];
    onClose: () => void;
    onUpload: (files: File[], note: string) => void;
}

export function ImageUploadModal(props: ImageUploadModalProps) {
    const { open, files, onClose, onUpload } = props;
    const [note, setNote] = useState("");
    const [previewUrls, setPreviewUrls] = useState<string[]>([]);
    const [loadedIndexes, setLoadedIndexes] = useState<Record<number, boolean>>({});

    useEffect(() => {
        if (files.length > 0) {
            const urls = files.map((file) => URL.createObjectURL(file));
            setPreviewUrls(urls);
            setLoadedIndexes({});

            return () => {
                urls.forEach((url) => URL.revokeObjectURL(url));
            };
        }

        setPreviewUrls([]);
        setLoadedIndexes({});
    }, [files]);

    const handleUpload = () => {
        onUpload(files, note);
        setNote("");
        onClose();
    };

    const handleClose = () => {
        previewUrls.forEach((url) => URL.revokeObjectURL(url));
        setNote("");
        setPreviewUrls([]);
        setLoadedIndexes({});
        onClose();
    };

    const isSingleFile = files.length === 1;

    return (
        <Modal open={open} onClose={handleClose}>
            <ModalDialog
                layout="center"
                variant="soft"
                sx={{
                    maxWidth: "min(960px, 94vw)",
                    width: "100%",
                    p: 0,
                    gap: 0,
                    overflow: "hidden",
                    borderRadius: "4px",
                }}
            >
                <DialogTitle sx={{ px: 2, pt: 2, pb: 1.5, m: 0 }}>
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        width="100%"
                    >
                        <Typography level="title-md">
                            Upload {files.length > 1 ? `${files.length} Photos` : "Photo"}
                        </Typography>
                        <Button
                            variant="plain"
                            color="neutral"
                            onClick={handleClose}
                            sx={{ minHeight: "auto", p: 0.5 }}
                        >
                            <X size={18} />
                        </Button>
                    </Box>
                </DialogTitle>

                <Divider />

                <DialogContent sx={{ p: 0, overflow: "hidden", gap: 0 }}>
                    {previewUrls.length > 0 && (
                        <Box
                            sx={{
                                position: "relative",
                                display: "flex",
                                justifyContent: "center",
                                alignItems: "center",
                                width: "100%",
                                minHeight: isSingleFile ? 280 : 180,
                                maxHeight: isSingleFile ? "min(72vh, 720px)" : 320,
                                overflow: isSingleFile ? "hidden" : "auto",
                                bgcolor: "background.level2",
                                ...(isSingleFile
                                    ? {}
                                    : {
                                          display: "grid",
                                          gridTemplateColumns: "repeat(auto-fill, minmax(140px, 1fr))",
                                          gap: 0.5,
                                          p: 0.5,
                                          alignContent: "start",
                                      }),
                            }}
                        >
                            {previewUrls.map((url, index) => (
                                <Box
                                    key={url}
                                    sx={{
                                        position: "relative",
                                        width: "100%",
                                        ...(isSingleFile
                                            ? {
                                                  minHeight: 280,
                                                  maxHeight: "min(72vh, 720px)",
                                              }
                                            : {
                                                  aspectRatio: "1/1",
                                                  overflow: "hidden",
                                              }),
                                    }}
                                >
                                    {!loadedIndexes[index] && (
                                        <Skeleton
                                            variant="rectangular"
                                            animation="wave"
                                            sx={{
                                                position: "absolute",
                                                inset: 0,
                                                width: "100%",
                                                height: "100%",
                                                borderRadius: 0,
                                            }}
                                        />
                                    )}
                                    <Box
                                        component="img"
                                        src={url}
                                        alt={`Preview ${index + 1}`}
                                        onLoad={() =>
                                            setLoadedIndexes((prev) => ({
                                                ...prev,
                                                [index]: true,
                                            }))
                                        }
                                        sx={{
                                            display: "block",
                                            width: "100%",
                                            height: isSingleFile ? "auto" : "100%",
                                            maxHeight: isSingleFile ? "min(72vh, 720px)" : "none",
                                            objectFit: isSingleFile ? "contain" : "cover",
                                            opacity: loadedIndexes[index] ? 1 : 0,
                                            transition: "opacity 0.25s ease",
                                        }}
                                    />
                                </Box>
                            ))}
                        </Box>
                    )}

                    <Box
                        sx={{
                            width: "100%",
                            borderTop: "1px solid",
                            borderColor: "divider",
                            px: 2,
                            py: 1.5,
                            bgcolor: "background.surface",
                        }}
                    >
                        <Box display="flex" alignItems="center" gap={0.75} mb={0.75}>
                            <MessageSquareText size={14} />
                            <Typography level="body-sm" sx={{ fontWeight: 600 }}>
                                Note
                            </Typography>
                            <Typography level="body-xs" sx={{ color: "text.tertiary" }}>
                                (optional)
                            </Typography>
                        </Box>
                        <Textarea
                            placeholder="Add a note about these photos..."
                            value={note}
                            onChange={(e) => setNote(e.target.value)}
                            minRows={2}
                            maxRows={4}
                            sx={{
                                width: "100%",
                                borderRadius: "4px",
                            }}
                        />
                    </Box>
                </DialogContent>

                <Divider />

                <DialogActions sx={{ px: 2, py: 1.5, m: 0 }}>
                    <Button variant="outlined" color="neutral" onClick={handleClose}>
                        Cancel
                    </Button>
                    <Button variant="soft" onClick={handleUpload}>
                        Upload
                    </Button>
                </DialogActions>
            </ModalDialog>
        </Modal>
    );
}
